// src/components/ReporteAsistenciaMensual.js
// Reporte de asistencia mensual tipo Excel con jugadores x fechas
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Download, Printer, Calendar, Users } from 'lucide-react';
import FeaturedLayout from './layouts/FeaturedLayout';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  getAllGroups,
  getAsistenciaMensual,
  getSeriesEntrenamiento,
} from '../lib/supabase';
const ReporteAsistenciaMensual = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [grupos, setGrupos] = useState([]);
  const [series, setSeries] = useState([]);
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [selectedSerie, setSelectedSerie] = useState(null);
  const [selectedMes, setSelectedMes] = useState(new Date().getMonth() + 1);
  const [selectedAnio, setSelectedAnio] = useState(new Date().getFullYear());
  const [reporteData, setReporteData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const printRef = useRef(null);
  // Cargar grupos
  useEffect(() => {
    const loadGrupos = async () => {
      try {
        const { data, error } = await getAllGroups();
        if (error) throw error;
        setGrupos(data || []);
        // Leer parametros de URL
        const grupoIdFromUrl = searchParams.get('grupo');
        const mesFromUrl = searchParams.get('mes');
        const yearFromUrl = searchParams.get('year');
        // Pre-seleccionar grupo desde URL
        if (grupoIdFromUrl && data) {
          const grupoEncontrado = data.find(g => g.id === grupoIdFromUrl);
          if (grupoEncontrado) {
            setSelectedGrupo(grupoEncontrado);
          }
        } else if (data && data.length === 1) {
          // Auto-seleccionar primer grupo si hay solo uno
          setSelectedGrupo(data[0]);
        }
        // Pre-seleccionar mes y anio desde URL
        if (mesFromUrl) {
          setSelectedMes(parseInt(mesFromUrl));
        }
        if (yearFromUrl) {
          setSelectedAnio(parseInt(yearFromUrl));
        }
      } catch (error) {
        console.error('Error cargando grupos:', error);
      }
    };
    loadGrupos();
  }, [searchParams]);
  // Cargar series cuando se selecciona un grupo
  useEffect(() => {
    const loadSeries = async () => {
      if (!selectedGrupo) {
        setSeries([]);
        return;
      }
      try {
        const { data, error } = await getSeriesEntrenamiento(selectedGrupo.id, true);
        if (error) throw error;
        setSeries(data || []);
      } catch (error) {
        console.error('Error cargando series:', error);
        setSeries([]);
      }
    };
    loadSeries();
  }, [selectedGrupo]);
  // Cargar reporte
  const loadReporte = useCallback(async () => {
    if (!selectedGrupo || !selectedMes || !selectedAnio) {
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await getAsistenciaMensual(
        selectedGrupo.id,
        selectedMes,
        selectedAnio,
        selectedSerie?.id || null
      );
      if (error) throw error;
      setReporteData(data);
    } catch (error) {
      console.error('Error cargando reporte:', error);
      setReporteData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedGrupo, selectedMes, selectedAnio, selectedSerie]);
  // Recargar reporte cuando cambian los filtros
  useEffect(() => {
    loadReporte();
  }, [loadReporte]);
  // Exportar a Excel (HTML .xls)
  const handleExportExcel = () => {
    if (isExporting) return;
    if (!reporteData || !reporteData.jugadores || !reporteData.eventos) return;
    setIsExporting(true);
    const { jugadores, eventos } = reporteData;
    const escapeHtml = (value) => {
      if (value === null || value === undefined) return '';
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    };
    const safeGroupName = (selectedGrupo?.nombre_del_grupo || 'grupo')
      .replace(/[^a-zA-Z0-9-_]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .slice(0, 40);
    const headerRow1 = `
      <tr>
        <th rowSpan="2">Número</th>
        <th rowSpan="2">Nombre completo</th>
        ${eventos.map((evento) => {
          const fecha = new Date(`${evento.fecha}T00:00:00`);
          const nombreDiaCompleto = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'][fecha.getDay()];
          return `<th>${nombreDiaCompleto}</th>`;
        }).join('')}
        <th rowSpan="2">Total</th>
        <th rowSpan="2">P</th>
        <th rowSpan="2">R</th>
        <th rowSpan="2">F</th>
        <th rowSpan="2">%</th>
      </tr>
    `;
    const headerRow2 = `
      <tr>
        ${eventos.map((evento) => {
          const fecha = new Date(`${evento.fecha}T00:00:00`);
          const dia = fecha.getDate();
          const mes = fecha.getMonth() + 1;
          return `<th>${dia}/${mes}/${selectedAnio}</th>`;
        }).join('')}
      </tr>
    `;
    const rows = jugadores.map(j => {
      const asistencias = eventos.map(e => {
        const a = j.asistencias[e.fecha];
        if (!a) return { label: '-', cls: 'cell-none' };
        if (a.estado === 'asistio') return { label: 'P', cls: 'cell-p' };
        if (a.estado === 'retardo') return { label: 'R', cls: 'cell-r' };
        if (a.estado === 'ausente') return { label: 'F', cls: 'cell-f' };
        if (a.estado === 'justificado') return { label: 'J', cls: 'cell-j' };
        if (a.estado === 'exento') return { label: 'E', cls: 'cell-e' };
        return { label: '-', cls: 'cell-none' };
      });
      const fixedCells = [
        `<td>${escapeHtml(j.jugador.numero || '')}</td>`,
        `<td>${escapeHtml(j.jugador.nombre_completo || '')}</td>`,
      ];
      const asistenciaCells = asistencias.map(cell => `<td class="${cell.cls}">${escapeHtml(cell.label)}</td>`);
      const totalCells = [
        `<td class="cell-total">${escapeHtml(j.estadisticas.total)}</td>`,
        `<td class="cell-total-p">${escapeHtml(j.estadisticas.asistio)}</td>`,
        `<td class="cell-total-r">${escapeHtml(j.estadisticas.retardo || 0)}</td>`,
        `<td class="cell-total-f">${escapeHtml(j.estadisticas.falto)}</td>`,
        `<td class="cell-total-pct">${escapeHtml(`${j.estadisticas.porcentaje}%`)}</td>`
      ];
      return `<tr>${[...fixedCells, ...asistenciaCells, ...totalCells].join('')}</tr>`;
    }).join('');
    const html = `<!DOCTYPE html>
<html>
  <head>
    <meta charset="utf-8" />
    <style>
      body { font-family: Arial, sans-serif; font-size: 10pt; }
      table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 10pt; }
      th, td { border: 1px solid #ccc; padding: 6px 8px; font-size: 10pt; }
      th { background: #f1f5f9; font-weight: 700; }
      thead tr:first-child th { background: #e2e8f0; }
      .header-title { font-size: 12pt; font-weight: 700; margin-bottom: 2px; }
      .header-sub { font-size: 10pt; color: #475569; margin-bottom: 8px; }
      .header-meta { font-size: 10pt; color: #475569; margin-bottom: 12px; }
      .cell-p { background: #bbf7d0; color: #166534; font-weight: bold; text-align: center; }
      .cell-r { background: #fde68a; color: #92400e; font-weight: bold; text-align: center; }
      .cell-f { background: #fecaca; color: #991b1b; font-weight: bold; text-align: center; }
      .cell-j { background: #fde047; color: #854d0e; font-weight: bold; text-align: center; }
      .cell-e { background: #e2e8f0; color: #475569; font-weight: bold; text-align: center; }
      .cell-none { color: #94a3b8; text-align: center; }
      .cell-total { background: #eff6ff; font-weight: bold; text-align: center; }
      .cell-total-p { background: #ecfdf3; font-weight: bold; text-align: center; }
      .cell-total-r { background: #fffbeb; font-weight: bold; text-align: center; }
      .cell-total-f { background: #fef2f2; font-weight: bold; text-align: center; }
      .cell-total-pct { background: #fefce8; font-weight: bold; text-align: center; }
      .legend { margin-top: 12px; font-size: 10pt; }
      .legend-item { display: inline-block; margin-right: 12px; }
    </style>
  </head>
  <body>
    <div class="header-title">${escapeHtml(selectedGrupo?.nombre_del_grupo || '')}</div>
    <div class="header-sub">${escapeHtml(meses.find(m => m.value === selectedMes)?.label || '')} ${escapeHtml(selectedAnio)}</div>
    <div class="header-meta">${escapeHtml(reporteData?.jugadores?.length || 0)} jugadores${selectedSerie ? ` - ${escapeHtml(selectedSerie.nombre)}` : ''}</div>
    <table>
      <thead>
        ${headerRow1}
        ${headerRow2}
      </thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="legend">
      <span class="legend-item"><span class="cell-p">P</span> Asistio</span>
      <span class="legend-item"><span class="cell-r">R</span> Retardo</span>
      <span class="legend-item"><span class="cell-f">F</span> Falto</span>
      <span class="legend-item"><span class="cell-j">J</span> Justificado</span>
      <span class="legend-item"><span class="cell-e">E</span> Exento</span>
      <span class="legend-item"><span class="cell-none">-</span> Sin registrar</span>
    </div>
  </body>
</html>`;
    const blob = new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `asistencia_${safeGroupName}_${selectedMes}_${selectedAnio}.xls`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => setIsExporting(false), 400);
  };
  // Imprimir
  const handlePrint = () => {
    if (!reporteData || !selectedGrupo) return;
    const { jugadores, eventos } = reporteData;
    const printWindow = window.open('', '_blank', 'width=1200,height=900');
    if (!printWindow) return;
    const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    const headerRow1 = `
      <tr>
        <th rowSpan="2">Número</th>
        <th rowSpan="2">Nombre completo</th>
        ${eventos.map((evento) => {
          const fecha = new Date(`${evento.fecha}T00:00:00`);
          const nombreDiaCompleto = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'][fecha.getDay()];
          return `<th>${nombreDiaCompleto}</th>`;
        }).join('')}
        <th rowSpan="2">Total</th>
        <th rowSpan="2">P</th>
        <th rowSpan="2">R</th>
        <th rowSpan="2">F</th>
        <th rowSpan="2">%</th>
      </tr>
    `;
    const headerRow2 = `
      <tr>
        ${eventos.map((evento) => {
          const fecha = new Date(`${evento.fecha}T00:00:00`);
          const dia = fecha.getDate();
          const mes = fecha.getMonth() + 1;
          return `<th>${dia}/${mes}/${selectedAnio}</th>`;
        }).join('')}
      </tr>
    `;
    const cellStyle = (estado) => {
      if (estado === 'asistio') return 'background:#bbf7d0;color:#166534;font-weight:700;';
      if (estado === 'retardo') return 'background:#fde68a;color:#92400e;font-weight:700;';
      if (estado === 'ausente') return 'background:#fecaca;color:#991b1b;font-weight:700;';
      if (estado === 'justificado') return 'background:#fde047;color:#854d0e;font-weight:700;';
      if (estado === 'exento') return 'background:#e2e8f0;color:#475569;font-weight:700;';
      return 'color:#94a3b8;';
    };
    const bodyRows = jugadores.map((jugador) => {
      const cells = eventos.map((evento) => {
        const asistencia = jugador.asistencias[evento.fecha];
        const estado = asistencia?.estado || 'sin_registrar';
        const letra = estado === 'asistio'
          ? 'P'
          : estado === 'retardo'
            ? 'R'
            : estado === 'ausente'
              ? 'F'
              : estado === 'justificado'
                ? 'J'
                : estado === 'exento'
                  ? 'E'
                  : '-';
        return `<td style="${cellStyle(estado)}">${letra}</td>`;
      }).join('');
      return `
        <tr>
          <td>${escapeHtml(jugador.jugador.numero || '-')}</td>
          <td class="text-left">${escapeHtml(jugador.jugador.nombre_completo)}</td>
          ${cells}
          <td style="background:#eff6ff;font-weight:700;">${jugador.estadisticas.total}</td>
          <td style="background:#ecfdf3;font-weight:700;">${jugador.estadisticas.asistio}</td>
          <td style="background:#fffbeb;font-weight:700;">${jugador.estadisticas.retardo || 0}</td>
          <td style="background:#fef2f2;font-weight:700;">${jugador.estadisticas.falto}</td>
          <td style="background:#fefce8;font-weight:700;">${jugador.estadisticas.porcentaje}%</td>
        </tr>
      `;
    }).join('');
    const html = `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Reporte de Asistencia</title>
    <style>
      @page { size: portrait; margin: 6mm; }
      body { margin: 0; font-family: Arial, sans-serif; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      h1 { font-size: 14px; margin: 0 0 2px 0; }
      h2 { font-size: 11px; margin: 0 0 8px 0; font-weight: normal; }
      table { width: 100%; border-collapse: collapse; table-layout: fixed; }
      th, td { border: 1px solid #cbd5e1; padding: 3px 4px; font-size: 9px; text-align: center; }
      th { background: #f1f5f9; font-weight: 700; }
      thead tr:first-child th { background: #e2e8f0; }
      td.text-left { text-align: left; }
    </style>
  </head>
  <body>
    <h1>${escapeHtml(selectedGrupo.nombre_del_grupo)}</h1>
    <h2>${meses.find(m => m.value === selectedMes)?.label} ${selectedAnio}${selectedSerie ? ` - ${escapeHtml(selectedSerie.nombre)}` : ''}</h2>
    <table>
      <thead>
        ${headerRow1}
        ${headerRow2}
      </thead>
      <tbody>
        ${bodyRows}
      </tbody>
    </table>
  </body>
</html>`;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.onload = () => {
      printWindow.print();
      printWindow.close();
    };
  };
  const handleGoToAttendance = useCallback((fecha) => {
    if (!selectedGrupo) return;
    navigate(`/attendance/${selectedGrupo.id}`, {
      state: {
        group: selectedGrupo,
        mode: 'reporte',
        fecha
      }
    });
  }, [navigate, selectedGrupo]);
  // Renderizar celda de asistencia
  const renderAsistenciaCell = (estado) => {
    const styles = {
      'asistio': 'bg-green-500 text-white border-gray-300',
      'retardo': 'bg-amber-500 text-white border-gray-300',
      'ausente': 'bg-red-500 text-white border-gray-300',
      'justificado': 'bg-yellow-400 text-gray-800 border-gray-300',
      'exento': 'bg-slate-200 text-slate-700 border-gray-300',
      'sin_registrar': 'bg-white text-gray-400 border-gray-300'
    };
    const content = {
      'asistio': 'P',
      'retardo': 'R',
      'ausente': 'F',
      'justificado': 'J',
      'exento': 'E',
      'sin_registrar': '-'
    };
    return (
      <td className={`border ${styles[estado]} text-center p-2 text-sm font-medium`}>
        {content[estado]}
      </td>
    );
  };
  const meses = [
    { value: 1, label: 'Enero' },
    { value: 2, label: 'Febrero' },
    { value: 3, label: 'Marzo' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Mayo' },
    { value: 6, label: 'Junio' },
    { value: 7, label: 'Julio' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Septiembre' },
    { value: 10, label: 'Octubre' },
    { value: 11, label: 'Noviembre' },
    { value: 12, label: 'Diciembre' },
  ];
  const anios = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i);
  return (
    <FeaturedLayout
      title="Reporte de Asistencia Mensual"
      subtitle="Vista de asistencias por jugador y fecha"
      refreshAction={loadReporte}
    >
      <style>{`
        @media print {
          @page { size: portrait; margin: 0; }
          html, body { width: 100% !important; margin: 0 !important; padding: 0 !important; }
          body * { visibility: hidden !important; }
          .report-print-area, .report-print-area * { visibility: visible !important; }
          .report-print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: auto;
            margin: 0 !important;
            padding: 0 !important;
          }
          .report-print-area { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .report-print-area .no-print { display: none !important; }
          .report-print-area .print-scale {
            zoom: 1;
          }
          .report-print-area table { width: 100% !important; min-width: 100% !important; }
          .report-print-area table { table-layout: fixed; }
          .report-print-area th,
          .report-print-area td { font-size: 9px !important; padding: 3px 4px !important; }
          .report-print-area th,
          .report-print-area td { word-break: break-word; }
          .report-print-area .sticky { position: static !important; }
          .report-print-area .overflow-x-auto { overflow: visible !important; }
          .report-print-area .card { width: 100% !important; max-width: none !important; margin: 0 !important; }
          .report-print-area .card-content { padding: 0 !important; }
          .report-print-area .card { box-shadow: none !important; }
        }
      `}</style>
      {/* Filtros */}
      <Card className="mb-6 no-print">
        <CardHeader>
          <CardTitle>Filtros del Reporte</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
            {/* Grupo */}
            <div className="space-y-2">
              <Label>Grupo</Label>
              <Select
                value={selectedGrupo?.id || ''}
                onValueChange={(value) => {
                  const grupo = grupos.find(g => g.id === value);
                  setSelectedGrupo(grupo);
                  setSelectedSerie(null);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar grupo" />
                </SelectTrigger>
                <SelectContent>
                  {grupos.map((grupo) => (
                    <SelectItem key={grupo.id} value={grupo.id}>
                      {grupo.nombre_del_grupo}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Mes */}
            <div className="space-y-2">
              <Label>Mes</Label>
              <Select
                value={String(selectedMes)}
                onValueChange={(value) => setSelectedMes(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {meses.map((mes) => (
                    <SelectItem key={mes.value} value={String(mes.value)}>
                      {mes.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Anio */}
            <div className="space-y-2">
              <Label>Anio</Label>
              <Select
                value={String(selectedAnio)}
                onValueChange={(value) => setSelectedAnio(parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {anios.map((anio) => (
                    <SelectItem key={anio} value={String(anio)}>
                      {anio}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Serie (opcional) */}
            <div className="space-y-2">
              <Label>Serie (Opcional)</Label>
              <Select
                value={selectedSerie?.id || 'todas'}
                onValueChange={(value) => {
                  const serie = value === 'todas' ? null : series.find(s => s.id === value);
                  setSelectedSerie(serie);
                }}
                disabled={series.length === 0}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todas las series" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas las series</SelectItem>
                  {series.map((serie) => (
                    <SelectItem key={serie.id} value={serie.id}>
                      {serie.nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {/* Acciones */}
            <div className="space-y-2 lg:col-span-2 xl:col-span-2">
              <Label>&nbsp;</Label>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={handleExportExcel}
                  disabled={isExporting || !reporteData || reporteData.jugadores.length === 0}
                  className="flex-1 min-w-[120px]"
                >
                  <Download className="h-4 w-4 mr-1" />
                  Excel
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  onClick={handlePrint}
                  disabled={!reporteData || reporteData.jugadores.length === 0}
                  className="flex-1 min-w-[120px]"
                >
                  <Printer className="h-4 w-4 mr-1" />
                  Imprimir
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      {/* Tabla de reporte */}
      {loading ? (
        <div className="flex items-center justify-center py-32">
          <div className="text-center">
            <div className="h-8 w-8 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground font-medium">Cargando reporte...</p>
          </div>
        </div>
      ) : !reporteData || !reporteData.jugadores || reporteData.jugadores.length === 0 ? (
        <Card>
          <CardContent className="py-32">
            <div className="text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No hay datos para mostrar</h3>
              <p className="text-muted-foreground">
                {!selectedGrupo
                  ? 'Selecciona un grupo para ver el reporte'
                  : 'No hay eventos registrados para este período'}
              </p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="report-print-area" ref={printRef}>
          <div className="print-scale">
          <Card className="overflow-hidden">
            <CardHeader className="print:hidden">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{selectedGrupo.nombre_del_grupo}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {meses.find(m => m.value === selectedMes)?.label} {selectedAnio}
                    {selectedSerie && ` - ${selectedSerie.nombre}`}
                  </p>
                </div>
                <Badge variant="outline" className="text-base">
                  <Users className="h-4 w-4 mr-2" />
                  {reporteData.jugadores.length} jugadores
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th rowSpan="2" className="border p-2 text-center sticky left-0 bg-muted z-10">NUMERO</th>
                      <th rowSpan="2" className="border p-2 text-center">NOMBRE COMPLETO</th>
                      {reporteData.eventos.map((evento) => {
                        const fecha = new Date(evento.fecha + 'T00:00:00');
                        const nombreDiaCompleto = ['DOMINGO', 'LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO'][fecha.getDay()];
                        return (
                          <th key={evento.id} className="border p-1 text-center min-w-[60px]">
                            <div className="text-xs font-semibold">{nombreDiaCompleto}</div>
                          </th>
                        );
                      })}
                      <th rowSpan="2" className="border p-2 text-center bg-blue-50 font-bold">Total</th>
                      <th rowSpan="2" className="border p-2 text-center bg-green-50 font-bold">P</th>
                      <th rowSpan="2" className="border p-2 text-center bg-amber-50 font-bold">R</th>
                      <th rowSpan="2" className="border p-2 text-center bg-red-50 font-bold">F</th>
                      <th rowSpan="2" className="border p-2 text-center bg-yellow-50 font-bold">%</th>
                    </tr>
                    <tr>
                      {reporteData.eventos.map((evento) => {
                        const fecha = new Date(evento.fecha + 'T00:00:00');
                        const dia = fecha.getDate();
                        const mes = fecha.getMonth() + 1;
                        return (
                          <th key={`fecha-${evento.id}`} className="border p-1 text-center">
                            <button
                              type="button"
                              className="font-semibold text-blue-600 hover:underline text-xs disabled:text-muted-foreground"
                              onClick={() => handleGoToAttendance(evento.fecha)}
                              disabled={!selectedGrupo}
                            >
                              {dia}/{mes}/{selectedAnio}
                            </button>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {reporteData.jugadores.map((jugador) => {
                      return (
                      <tr key={jugador.jugador.id} className="hover:bg-accent/50">
                        <td className="border p-2 sticky left-0 bg-background z-10 text-center">
                          <div className="font-medium">{jugador.jugador.numero || '-'}</div>
                        </td>
                        <td className="border p-2 text-left">
                          <div className="font-medium">{jugador.jugador.nombre_completo}</div>
                        </td>
                        {reporteData.eventos.map((evento) => {
                          const asistencia = jugador.asistencias[evento.fecha];
                          return (
                            <React.Fragment key={evento.id}>
                              {renderAsistenciaCell(asistencia?.estado || 'sin_registrar')}
                            </React.Fragment>
                          );
                        })}
                        <td className="border p-2 text-center font-medium bg-blue-50">
                          {jugador.estadisticas.total}
                        </td>
                        <td className="border p-2 text-center font-medium bg-green-50">
                            {jugador.estadisticas.asistio}
                          </td>
                          <td className="border p-2 text-center font-medium bg-amber-50">
                            {jugador.estadisticas.retardo || 0}
                          </td>
                          <td className="border p-2 text-center font-medium bg-red-50">
                            {jugador.estadisticas.falto}
                          </td>
                          <td className="border p-2 text-center font-bold bg-yellow-50">
                            {jugador.estadisticas.porcentaje}%
                          </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
          {/* Leyenda */}
          {reporteData && reporteData.jugadores.length > 0 && (
            <Card className="mt-6 no-print">
              <CardContent className="pt-6">
                <div className="flex flex-wrap items-center gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-green-100 border border-green-200 flex items-center justify-center text-green-800 font-bold">
                      P
                    </div>
                    <span>Asistio</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-amber-100 border border-amber-200 flex items-center justify-center text-amber-800 font-bold">
                      R
                    </div>
                    <span>Retardo</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-red-100 border border-red-200 flex items-center justify-center text-red-800 font-bold">
                      F
                    </div>
                    <span>Falto</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-yellow-100 border border-yellow-200 flex items-center justify-center text-yellow-800 font-bold">
                      J
                    </div>
                    <span>Justificado</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-slate-200 border border-slate-300 flex items-center justify-center text-slate-700 font-bold">
                      E
                    </div>
                    <span>Exento</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <div className="w-6 h-6 rounded bg-gray-50 border border-gray-200 flex items-center justify-center text-gray-400 font-bold">
                      -
                    </div>
                    <span>Sin registrar</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          </div>
        </div>
      )}
    </FeaturedLayout>
  );
};
export default ReporteAsistenciaMensual;

